import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { chevronRight, clock, flashSlash, messageWhite } from 'assets/images';
import { TipoVarianteDetalleEnum } from 'config';
import { TrackingCorteEstadosResponseModel } from 'services/models';
import {
  FORMATO_DIA_MES_ANIO,
  formatHoraNotificacionCorte,
  formatTerDisplay,
  parseFlexibleDate,
  TIPO_FECHA_HOY,
  TIPO_FECHA_MANANA,
} from 'utils/formatters/dateFormatter';
import { datoNoValido } from 'utils/colorBallsUtils';
import { CADENA_VACIA } from 'utils/constants';
import { buildTituloCorteProgramado } from 'utils/outageDataMapper';
import DetalleAvance from './DetalleAvance';
import OutageHorariosConDisclaimer from './OutageHorariosConDisclaimer';
import OutageReportarBox from './OutageReportarBox';
import useStyles from './OutageDashboardStyles';
import {
  ESTADO_NODO_ACTIVO,
  ESTADO_NODO_PENDIENTE,
  MARCADOR_CUANDO,
  TEST_ID_TIMELINE_FUTURO,
  TEST_ID_AVANCE_FUTURO,
  TEST_ID_BOTON_REPORTAR_FUTURO,
  TEST_ID_DASHBOARD_FUTURO,
  TEST_ID_DISCLAIMER_FUTURO,
  TEST_ID_FOOTER_FUTURO,
  TEST_ID_HORARIOS_FUTURO,
  TEST_ID_HORA_NOTIFICACION_FUTURO,
  TEST_ID_REPORTAR_FUTURO,
  TEST_ID_TITULO_FUTURO,
  TEST_ID_TOGGLE_DETALLE_FUTURO,
} from './outageDashboard.constants';
import { buildFooterTexto, buildTerLabels } from './outageDashboard.helpers';
import useDetalleAvanceToggle from './useDetalleAvanceToggle';

/**
 * Dashboard de próximo corte programado (corte futuro).
 * @param data respuesta de tracking del corte
 * @param numeroOrden identificador de la orden
 * @param comuna comuna del polígono para el motivo
 * @param onDetalleAvanceChange avisa al modal cuando se abre el detalle
 */
const OutageDashboardFuturo = ({
  data,
  numeroOrden,
  comuna,
  onDetalleAvanceChange,
}: {
  data: TrackingCorteEstadosResponseModel;
  numeroOrden: string;
  comuna?: string;
  onDetalleAvanceChange?: (abierto: boolean) => void;
}) => {
  /**
   * Variable que maneja el uso de estilos.
   */
  const styles = useStyles();

  /**
   * Uso del traductor.
   */
  const { t } = useTranslation();

  /**
   * Estado que indica si el detalle de avance está abierto.
   */
  const { detalleAbierto, setDetalleAbierto } = useDetalleAvanceToggle(onDetalleAvanceChange);

  const terLabels = buildTerLabels(t);
  const inicioDisplay = formatTerDisplay(data.fechaInicioCorte, terLabels);
  const finDisplay = formatTerDisplay(data.fechaFinCorte, terLabels);
  const inicioFecha = parseFlexibleDate(data.fechaInicioCorte);

  let cuando = CADENA_VACIA;
  if (inicioDisplay.kind === TIPO_FECHA_HOY) {
    cuando = t('desconexion:paginas.mapaCortes.modalTracking.dashboard.futuro.cuandoHoy');
  } else if (inicioDisplay.kind === TIPO_FECHA_MANANA) {
    cuando = t('desconexion:paginas.mapaCortes.modalTracking.dashboard.futuro.cuandoManana');
  } else if (inicioFecha) {
    cuando = `${t('desconexion:paginas.mapaCortes.modalTracking.dashboard.futuro.cuandoFecha')} ${format(
      inicioFecha,
      FORMATO_DIA_MES_ANIO
    )}`;
  }

  const plantillaTitulo = t('desconexion:paginas.mapaCortes.modalTracking.dashboard.futuro.titulo', { cuando: MARCADOR_CUANDO });
  const titulo = cuando
    ? plantillaTitulo.replace(MARCADOR_CUANDO, cuando)
    : t('desconexion:paginas.mapaCortes.modalTracking.dashboard.futuro.pastilla');

  const motivo = buildTituloCorteProgramado(comuna, {
    conComuna: c => t('desconexion:paginas.mapaCortes.modalTracking.dashboard.futuro.motivoConComuna', { comuna: c }),
    sinComuna: t('desconexion:paginas.mapaCortes.modalTracking.dashboard.futuro.motivoSinComuna'),
  });

  /**
   * Si la notificación no es el mismo día que el corte, la hora sola se confunde con ese día.
   */
  const horaNotificacion = formatHoraNotificacionCorte(data.fechaCreacionCaso, data.fechaInicioCorte);
  const footerTexto = buildFooterTexto(data.fechaActualizacion, t);

  const nodos = [
    { icono: messageWhite, kind: ESTADO_NODO_ACTIVO },
    { icono: flashSlash, kind: ESTADO_NODO_PENDIENTE },
    { icono: clock, kind: ESTADO_NODO_PENDIENTE },
  ];

  return (
    <div className={styles.root} data-testid={TEST_ID_DASHBOARD_FUTURO}>
      {detalleAbierto ? (
        <DetalleAvance
          data={data}
          numeroOrden={numeroOrden}
          comuna={comuna}
          variante={TipoVarianteDetalleEnum.FUTURO}
          onBack={() => setDetalleAbierto(false)}
        />
      ) : (
        <>
          <div className={styles.stickyCabecera}>
            <div className={styles.header}>
              <div className={styles.headerLeft}>
                <span className={`${styles.statusDot} ${styles.statusDotAzul}`} aria-hidden />
                <p className={styles.statusLabel}>{t('desconexion:paginas.mapaCortes.modalTracking.dashboard.futuro.pastilla')}</p>
              </div>
            </div>
          </div>

          <div className={styles.scrollArea}>
            <div className={styles.content}>
              <div className={styles.body}>
                <div className={`${styles.leftCol} ${styles.leftColFull}`}>
                  <h2 className={styles.motivo} data-testid={TEST_ID_TITULO_FUTURO}>
                    {titulo}
                  </h2>

                  <OutageHorariosConDisclaimer
                    inicioDisplay={inicioDisplay}
                    finDisplay={finDisplay}
                    testIdHorarios={TEST_ID_HORARIOS_FUTURO}
                    testIdDisclaimer={TEST_ID_DISCLAIMER_FUTURO}
                  />

                  <div className={`${styles.avanceBox} ${styles.avanceBoxAzul}`} data-testid={TEST_ID_AVANCE_FUTURO}>
                    <p className={`${styles.avanceTitulo} ${styles.avanceTituloAzul}`}>
                      {t('desconexion:paginas.mapaCortes.modalTracking.dashboard.futuro.notificacion')}
                    </p>

                    <div className={styles.timelineRow} data-testid={TEST_ID_TIMELINE_FUTURO}>
                      <div className={styles.timelineLineTrack} />
                      {nodos.map((nodo, index) => (
                        <div
                          // eslint-disable-next-line react/no-array-index-key
                          key={`futuro-step-${index}`}
                          className={`${styles.stepNode} ${nodo.kind === ESTADO_NODO_ACTIVO ? styles.stepActiveAzul : styles.stepPending}`}
                        >
                          <img src={nodo.icono} alt={CADENA_VACIA} className={styles.stepIcon} />
                        </div>
                      ))}
                    </div>

                    <div className={styles.estadoActualWrap}>
                      {!datoNoValido(horaNotificacion) && (
                        <p className={styles.estadoHora} data-testid={TEST_ID_HORA_NOTIFICACION_FUTURO}>
                          {horaNotificacion}
                        </p>
                      )}
                      <p className={styles.estadoDesc}>{motivo}</p>
                      <button
                        type="button"
                        className={styles.linkDetalle}
                        onClick={() => setDetalleAbierto(true)}
                        data-testid={TEST_ID_TOGGLE_DETALLE_FUTURO}
                      >
                        {t('desconexion:paginas.mapaCortes.modalTracking.dashboard.avance.verDetalle')}
                        <img src={chevronRight} alt={CADENA_VACIA} className={styles.iconoLinkDetalle} aria-hidden />
                      </button>
                    </div>
                  </div>

                  <OutageReportarBox
                    textoInfo={t('desconexion:paginas.mapaCortes.modalTracking.dashboard.futuro.reportarInfo')}
                    testIdBox={TEST_ID_REPORTAR_FUTURO}
                    testIdBoton={TEST_ID_BOTON_REPORTAR_FUTURO}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={styles.footer} data-testid={TEST_ID_FOOTER_FUTURO}>
            <p className={styles.footerText}>{footerTexto}</p>
          </div>
        </>
      )}
    </div>
  );
};

OutageDashboardFuturo.defaultProps = {
  comuna: CADENA_VACIA,
  onDetalleAvanceChange: undefined,
};

export default OutageDashboardFuturo;
